/**
 * Utility functions for formatting numbers, currency, and percentages
 * Used throughout the admin analytics interface
 */

/**
 * Format large numbers with K/M suffixes
 * Examples: 1000 -> 1K, 1500000 -> 1.5M
 */
export function formatNumber(num: number, decimals: number = 1): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(decimals) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(decimals) + 'K';
  }
  return num.toString();
}

/**
 * Format currency as USD
 * Examples: 1000 -> $1,000, 1500000 -> $1,500,000
 */
export function formatCurrency(amount: number, compact: boolean = false): string {
  if (compact) {
    if (amount >= 1000000) {
      return '$' + (amount / 1000000).toFixed(1) + 'M';
    }
    if (amount >= 1000) {
      return '$' + (amount / 1000).toFixed(1) + 'K';
    }
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Format percentage with optional decimal places
 * Examples: 0.156 -> 15.6%, 0.5 -> 50%
 */
export function formatPercentage(value: number, decimals: number = 1): string {
  return value.toFixed(decimals) + '%';
}

/**
 * Format number with commas for thousands separator
 * Examples: 1000 -> 1,000, 1500000 -> 1,500,000
 */
export function formatNumberWithCommas(num: number): string {
  return num.toLocaleString('en-US');
}

/**
 * Calculate percentage change between two values
 * Returns formatted string with + or - sign
 */
export function calculatePercentageChange(current: number, previous: number): string {
  if (previous === 0) return '0%';
  const change = ((current - previous) / previous) * 100;
  const sign = change > 0 ? '+' : '';
  return sign + change.toFixed(1) + '%';
}

/**
 * Format duration in minutes to human readable string
 * Examples: 65 -> 1h 5m, 30 -> 30m
 */
export function formatDuration(minutes: number): string {
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  }
  return `${minutes}m`;
}
